import React, { useState, useEffect } from 'react';
import './Collection.css';
import NFTCard from '../components/NFTCard';
import WalletSearchBox from '../components/WalletSearchBox';
import useWalletFilter from '../hooks/useWalletFilter';

const OGCollection = () => {
  const wallet = useWalletFilter();
  const [nftData, setNftData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [rarityMethod, setRarityMethod] = useState('sniper');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [showOnlyExisting, setShowOnlyExisting] = useState(true);
  const [showCustoms, setShowCustoms] = useState(false);
  const [customsData, setCustomsData] = useState([]);
  const [selectedDrop, setSelectedDrop] = useState('');

  useEffect(() => {
    const loadNFTData = async () => {
      setLoading(true);
      try {
        // Load regular NFT data
        const response = await fetch('/mma_ranks.json');
        const data = await response.json();

        // Load custom NFTs data
        const customsResponse = await fetch('/Customs_metadata.json');
        const customsRaw = await customsResponse.json();

        // Transform customs data to match regular NFT structure
        const transformedCustoms = customsRaw.map((custom, index) => {
          // Extract custom number from name (e.g., "Money Minded Ape Custom #1" -> "1")
          const customMatch = custom.name.match(/Custom #(\d+)/);
          const customNumber = customMatch ? customMatch[1] : (index + 1).toString();

          return {
            name: custom.name,
            NFTokenID: custom.NFTokenID || '',
            attributes: custom.attributes || [{ trait_type: 'Type', value: 'Custom' }],
            // Add dummy rankings so they sort consistently
            rankings: {
              rarity_sniper: { rank: 0 },
              rarity_score: { rank: 0 },
              statistical_rarity: { rank: 0 },
              jaccard_distance: { rank: 0 },
              average_trait_rarity: { rank: 0 },
              trait_rarity_rating: { rank: 0 },
            },
            isCustom: true,
            customNumber: customNumber,
          };
        });

        // Sort customs by custom number
        transformedCustoms.sort((a, b) => parseInt(a.customNumber) - parseInt(b.customNumber));
        setCustomsData(transformedCustoms);

        // Sort regular data based on selected rarity method
        let sortedData;
        switch (rarityMethod) {
          case 'sniper':
            sortedData = [...data].sort(
              (a, b) => a.rankings.rarity_sniper.rank - b.rankings.rarity_sniper.rank
            );
            break;
          case 'statistical':
            sortedData = [...data].sort(
              (a, b) => a.rankings.statistical_rarity.rank - b.rankings.statistical_rarity.rank
            );
            break;
          case 'jaccard':
            sortedData = [...data].sort(
              (a, b) => a.rankings.jaccard_distance.rank - b.rankings.jaccard_distance.rank
            );
            break;
          case 'averageTrait':
            sortedData = [...data].sort(
              (a, b) => a.rankings.average_trait_rarity.rank - b.rankings.average_trait_rarity.rank
            );
            break;
          case 'traitRating':
            sortedData = [...data].sort(
              (a, b) => a.rankings.trait_rarity_rating.rank - b.rankings.trait_rarity_rating.rank
            );
            break;
          default:
            sortedData = [...data].sort(
              (a, b) => a.rankings.rarity_score.rank - b.rankings.rarity_score.rank
            );
        }

        setNftData(sortedData);
        setFilteredData(sortedData);
      } catch (error) {
        console.error('Error loading NFT data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadNFTData();
  }, [rarityMethod]);

  useEffect(() => {
    let filtered = nftData;
    let customsFiltered = customsData;

    // Wallet-address filter takes precedence over substring search.
    if (wallet.isWalletSearch) {
      filtered = filtered.filter((nft) => wallet.ownedIds.has((nft.NFTokenID || '').toUpperCase()));
      customsFiltered = customsFiltered.filter((nft) =>
        wallet.ownedIds.has((nft.NFTokenID || '').toUpperCase())
      );
    } else if (searchTerm) {
      filtered = filtered.filter(
        (nft) =>
          nft.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          nft.attributes.some((attr) => attr.value.toLowerCase().includes(searchTerm.toLowerCase()))
      );

      // Also filter customs by search term
      customsFiltered = customsFiltered.filter(
        (nft) =>
          nft.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          nft.attributes.some((attr) => attr.value.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filter by drop if selected
    if (selectedDrop) {
      const dropNumber = parseInt(selectedDrop);
      filtered = filtered.filter((nft) => nft.drop === dropNumber);
      customsFiltered = customsFiltered.filter((nft) => nft.drop === dropNumber);
    }

    // Filter by existing NFTs if toggle is on (based on NFTokenID presence)
    if (showOnlyExisting) {
      filtered = filtered.filter((nft) => nft.NFTokenID && nft.NFTokenID.trim() !== '');
      customsFiltered = customsFiltered.filter(
        (nft) => nft.NFTokenID && nft.NFTokenID.trim() !== ''
      );
    }

    // Combine customs and regular NFTs if customs toggle is on
    let finalFiltered;
    if (showCustoms) {
      finalFiltered = [...customsFiltered, ...filtered];
    } else {
      finalFiltered = filtered;
    }

    setFilteredData(finalFiltered);
    setCurrentPage(1);
  }, [
    searchTerm,
    nftData,
    customsData,
    showOnlyExisting,
    showCustoms,
    selectedDrop,
    wallet.isWalletSearch,
    wallet.ownedIds,
  ]);

  const handleRarityMethodChange = (method) => {
    setRarityMethod(method);
    setCurrentPage(1);
  };

  const handleShowOnlyExistingChange = (checked) => {
    setShowOnlyExisting(checked);
    setCurrentPage(1);
  };

  const handleShowCustomsChange = (checked) => {
    setShowCustoms(checked);
    setCurrentPage(1);
  };

  const handleDropFilterChange = (drop) => {
    setSelectedDrop(drop);
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredData.slice(startIndex, startIndex + itemsPerPage);

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className='collection-page'>
      <div className='collection-header'>
        <h1>OG Money Minded Apes</h1>
        <p>Explore the rarity rankings of our original collection</p>
      </div>

      <div className='collection-controls'>
        <div className='controls-row'>
          <div className='rarity-selector'>
            <label>Rarity Method:</label>
            <select
              value={rarityMethod}
              onChange={(e) => handleRarityMethodChange(e.target.value)}
              className='rarity-dropdown'
            >
              <option value='sniper'>Rarity Sniper</option>
              <option value='score'>Rarity Score</option>
              <option value='statistical'>Statistical Rarity</option>
              <option value='average'>Average Trait Rarity</option>
              <option value='jaccard'>Jaccard Distance</option>
              <option value='traitRating'>Trait Rarity Rating</option>
            </select>
          </div>

          <div className='existing-toggle'>
            <label>
              <input
                type='checkbox'
                checked={showOnlyExisting}
                onChange={(e) => handleShowOnlyExistingChange(e.target.checked)}
              />
              Show only XLS20 NFTs
            </label>
          </div>

          <div className='customs-toggle'>
            <label>
              <input
                type='checkbox'
                checked={showCustoms}
                onChange={(e) => handleShowCustomsChange(e.target.checked)}
              />
              Show Custom NFTs
            </label>
          </div>

          <div className='items-per-page-selector'>
            <label>Items per page:</label>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className='items-per-page-dropdown'
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={500}>500</option>
            </select>
          </div>
        </div>

        <div className='search-and-pagination-row'>
          <div className='drop-filter'>
            <label>Filter by Drop:</label>
            <select
              value={selectedDrop}
              onChange={(e) => handleDropFilterChange(e.target.value)}
              className='drop-dropdown'
            >
              <option value=''>All Drops</option>
              <option value='1'>Drop 1 (OG #1-700, Custom #1-3)</option>
              <option value='2'>Drop 2 (OG #701-1040, Custom #4-6)</option>
              <option value='3'>Drop 3 (OG #1041-1380, Custom #7-9)</option>
              <option value='4'>Drop 4 (OG #1381-1630, Custom #10-11)</option>
              <option value='5'>Drop 5 (OG #1631-1730, Custom #12-13)</option>
            </select>
          </div>

          <WalletSearchBox
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            wallet={wallet}
          />
          
          <div className='pagination-info'>
            <span className='stat-item'>
              Page {currentPage} of {totalPages} ({itemsPerPage} per page)
            </span>
          </div>
        </div>

        <div className='collection-stats'>
          <span className='stat-item'>
            Total Items:{' '}
            {(nftData.length + (showCustoms ? customsData.length : 0)).toLocaleString()}
            {showCustoms && ` (${customsData.length} customs + ${nftData.length} regular)`}
          </span>
          <span className='stat-item'>Showing: {filteredData.length.toLocaleString()}</span>
          <span className='stat-item'>
            Current Method:{' '}
            {rarityMethod === 'sniper'
              ? 'Rarity Sniper'
              : rarityMethod === 'statistical'
              ? 'Statistical Rarity'
              : rarityMethod === 'average'
              ? 'Average Trait Rarity'
              : rarityMethod === 'score'
              ? 'Rarity Score'
              : rarityMethod === 'jaccard'
              ? 'Jaccard Distance'
              : rarityMethod === 'traitRating'
              ? 'Trait Rarity Rating'
              : 'Statistical Rarity'}
          </span>
          <span className='stat-item'>
            XLS20 NFTs:{' '}
            {(
              nftData.filter((nft) => nft.NFTokenID && nft.NFTokenID.trim() !== '').length +
              (showCustoms
                ? customsData.filter((nft) => nft.NFTokenID && nft.NFTokenID.trim() !== '').length
                : 0)
            ).toLocaleString()}
          </span>
          {showCustoms && (
            <span className='stat-item'>Custom NFTs: {customsData.length.toLocaleString()}</span>
          )}
          {selectedDrop && <span className='stat-item'>Filtered by: Drop {selectedDrop}</span>}
        </div>
      </div>

      {loading ? (
        <div className='loading-container'>
          <div className='loading-spinner'></div>
          <p>Loading collection data...</p>
        </div>
      ) : (
        <>
          <div className='nft-grid'>
            {currentItems.map((nft, index) => (
              <NFTCard
                key={`${nft.name}-${index}`}
                nft={nft}
                rarityMethod={rarityMethod}
                collection='og'
              />
            ))}
          </div>

          {totalPages > 1 && (
            <div className='pagination'>
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className='pagination-btn'
              >
                Previous
              </button>

              <div className='page-numbers'>
                {[...Array(Math.min(5, totalPages))].map((_, index) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = index + 1;
                  } else {
                    const start = Math.max(1, currentPage - 2);
                    pageNum = start + index;
                    if (pageNum > totalPages) pageNum = totalPages - (4 - index);
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => goToPage(pageNum)}
                      className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className='pagination-btn'
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OGCollection;
